import { useEffect, useRef, useState } from 'react';
import { PROJECTS, scrollToProject } from './WorksSection';
import { scrollToSection } from '../utils/scrollToSection';
import { useMediaQuery } from '../hooks';

// Engineering skills link to the projects that list them in their tech stack
// (matched against each project's `tech` string), so this stays in sync with
// the Selected Work section automatically.
const ENGINEERING = [
  { name: 'React', match: /react|next/i },
  { name: 'TypeScript', match: /typescript/i },
  { name: 'Next.js', match: /next/i },
  { name: 'GraphQL', match: /graphql/i },
  { name: 'REST APIs', match: /rest/i },
  { name: 'PostgreSQL', match: /postgres/i },
  { name: 'Neon' },
  { name: 'Firebase' },
  { name: 'HTML / CSS', all: true },
  { name: 'Responsive Design', all: true },
  { name: 'Performance' },
  { name: 'Testing' },
  { name: 'Data Migration' },
].map((skill) => ({
  ...skill,
  projects: skill.all
    ? PROJECTS
    : skill.match
      ? PROJECTS.filter((p) => skill.match.test(p.tech || ''))
      : [],
}));

// Impact skills link to the roles (and writing) they come from.
const DAVE_T = { label: 'Dave-T Engineering', section: 'about' };
const FEMNET = { label: 'FEMNET', section: 'about' };
const WRITING = { label: 'Stories', section: 'stories' };

const IMPACT = [
  { name: 'Human-Centred Design', via: [DAVE_T] },
  { name: 'Climate Adaptation', via: [DAVE_T] },
  { name: 'Gender-Responsive Design', via: [DAVE_T, FEMNET] },
  { name: 'Strategic Governance', via: [FEMNET] },
  { name: 'Policy Advocacy', via: [FEMNET, WRITING] },
  { name: 'Social Strategy', via: [FEMNET] },
  { name: 'Community Engagement', via: [FEMNET] },
];

const GROUPS = [
  { id: 'engineering', label: 'Engineering', skills: ENGINEERING },
  { id: 'impact', label: 'Impact', skills: IMPACT },
];

const LinkButton = ({ onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className="group inline-flex items-center gap-1.5 py-1.5 -my-1.5 text-charcoal hover:text-[#c93a22] transition-colors duration-300"
  >
    <span className="border-b border-charcoal/20 group-hover:border-[#c93a22] transition-colors duration-300">{children}</span>
    <span aria-hidden="true" className="text-xs transition-transform duration-300 group-hover:translate-x-0.5">→</span>
  </button>
);

export const Toolkit = () => {
  const canHover = useMediaQuery('(hover: hover) and (pointer: fine)');
  const [groupId, setGroupId] = useState('engineering');
  // One selected skill. Click/tap/focus selects immediately. Hover only
  // selects after the pointer rests on a word briefly, and the selection
  // stays when the pointer moves on, so sweeping across other words on the
  // way down to the links doesn't swap the panel out from under you.
  const [selected, setSelected] = useState(null);
  const hoverTimer = useRef(null);
  const HOVER_DELAY = 220;

  const cancelHover = () => clearTimeout(hoverTimer.current);
  const hoverSelect = (name) => {
    cancelHover();
    hoverTimer.current = setTimeout(() => setSelected(name), HOVER_DELAY);
  };
  useEffect(() => cancelHover, []);

  const group = GROUPS.find((g) => g.id === groupId);
  const activeName = selected;
  const active = group.skills.find((s) => s.name === activeName) || null;

  const switchGroup = (id) => {
    cancelHover();
    setGroupId(id);
    setSelected(null);
  };

  const renderDetail = () => {
    if (!active) {
      return (
        <p className="text-charcoal/60 font-light">
          {canHover ? 'Hover' : 'Tap'} a {groupId === 'engineering' ? 'skill to see the projects it shows up in' : 'focus to see where the work happens'}.
        </p>
      );
    }

    if (groupId === 'engineering') {
      if (active.projects.length === 0) {
        return (
          <p className="text-charcoal/70 font-light">
            Not in the projects above.{' '}
            <LinkButton onClick={() => scrollToSection('contact')}>Ask me about it</LinkButton>
          </p>
        );
      }
      return (
        <div>
          <p className="eyebrow mb-3">
            Seen in {active.projects.length === PROJECTS.length ? 'every project' : `${active.projects.length} project${active.projects.length > 1 ? 's' : ''}`}
          </p>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {(active.projects.length === PROJECTS.length ? [] : active.projects).map((p) => (
              <LinkButton key={p.id} onClick={() => scrollToProject(p.id)}>{p.name}</LinkButton>
            ))}
            {active.projects.length === PROJECTS.length && (
              <LinkButton onClick={() => scrollToSection('projects')}>See the work</LinkButton>
            )}
          </div>
        </div>
      );
    }

    return (
      <div>
        <p className="eyebrow mb-3">Through</p>
        <div className="flex flex-wrap gap-x-5 gap-y-2">
          {active.via.map((v) => (
            <LinkButton key={v.label} onClick={() => scrollToSection(v.section)}>{v.label}</LinkButton>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-4 mb-10">
        <h3 className="eyebrow">Toolkit</h3>

        {/* Engineering / Impact switch */}
        <div role="tablist" aria-label="Toolkit" className="relative grid grid-cols-2 shrink-0 rounded-full border border-charcoal/15 p-1">
          <span
            aria-hidden="true"
            className="absolute top-1 bottom-1 left-1 w-[calc(50%-4px)] rounded-full bg-charcoal transition-transform duration-500"
            style={{
              transform: `translateX(${groupId === 'impact' ? '100%' : '0'})`,
              transitionTimingFunction: 'cubic-bezier(0.19, 1, 0.22, 1)',
            }}
          />
          {GROUPS.map((g) => (
            <button
              key={g.id}
              type="button"
              role="tab"
              aria-selected={g.id === groupId}
              onClick={() => switchGroup(g.id)}
              className={`relative z-10 px-3 sm:px-4 py-1.5 text-[11px] uppercase tracking-[0.15em] font-semibold transition-colors duration-500 ${g.id === groupId ? 'text-white' : 'text-charcoal/60 hover:text-charcoal'}`}
            >
              {g.label}
            </button>
          ))}
        </div>
      </div>

      {/* Skills as words; re-keyed per group so they animate in on switch */}
      <ul
        key={groupId}
        role="tabpanel"
        className="flex flex-wrap gap-x-5 gap-y-1"
      >
        {group.skills.map((skill, i) => {
          const isActive = skill.name === activeName;
          const count = groupId === 'engineering' ? skill.projects.length : null;
          return (
            <li
              key={skill.name}
              style={{ animation: 'fade-in-up 0.6s ease-out backwards', animationDelay: `${i * 35}ms` }}
            >
              <button
                type="button"
                aria-pressed={isActive}
                onMouseEnter={() => canHover && hoverSelect(skill.name)}
                onMouseLeave={cancelHover}
                onFocus={() => setSelected(skill.name)}
                onClick={() => { cancelHover(); setSelected(skill.name); }}
                className={`font-grandslang text-[1.9rem] md:text-[2.4rem] leading-[1.2] text-left transition-all duration-300 ${
                  isActive ? 'text-[#ff6347] -translate-y-0.5' : activeName ? 'text-charcoal/35 hover:text-charcoal/70' : 'text-charcoal'
                }`}
              >
                {skill.name}
                {count > 0 && (
                  <sup className={`ml-1 font-sans text-[11px] font-semibold tabular-nums align-super transition-colors duration-300 ${isActive ? 'text-[#c93a22]' : 'text-charcoal/40'}`}>
                    {count}
                  </sup>
                )}
              </button>
            </li>
          );
        })}
      </ul>

      {/* What the active skill connects to. Fixed height so the page doesn't jump. */}
      <div className="mt-8 pt-6 border-t border-charcoal/10 min-h-[6.5rem]" aria-live="polite">
        {renderDetail()}
      </div>
    </div>
  );
};
